import React from 'react';
import { Layers } from 'lucide-react';
import dompurify from 'dompurify';
import { useTranslation } from 'react-i18next';
import { CollapsibleCard, CollapsibleCardContent } from 'src/components/ui/collapsible-card';
import { useToggle } from 'src/lib/state-utils';
import { RequestStatusBadge } from 'src/features/requests/request-status-badge';
import { useDecoratedRequestContext } from 'src/features/requests/use-decorated-request-context';
import { resolveDisplayedRequestStatus } from 'src/features/requests/request.helper';
import { totalLengthToString } from 'src/features/requests/request-fields/request-field.helper';
import { Label } from 'src/components/ui/label';
import { Skeleton } from 'src/components/ui/skeleton';
import { cn } from 'src/lib/utils';
import { Str } from 'src/helpers';
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';
import { SpaceErrorFallback } from 'src/features/fallback';
import { RequestOutcomesQueryContextProvider } from 'src/features/requests/request-outcome/use-request-outcomes-query-context';
import { deliverableOutcomesFilters } from 'src/features/requests/request-outcome/filter-queries';
import { RequestOutcomeResponse } from 'src/lib/services/api/request-api';
import { requestComplexityLabels } from 'src/api/services/RequestClient';
import { RequestPriorityBadge } from 'src/features/requests/request-priority-badge';

const Row: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  children,
  ...props
}) => (
  <div
    {...props}
    className={cn(
      'tw-flex tw-flex-col tw-gap-2 tw-border-b tw-border-neutral-100 tw-py-3 last:tw-border-b-0 md:tw-grid md:tw-grid-cols-3 md:tw-items-start md:tw-gap-6',
      className,
    )}
  >
    {children}
  </div>
);

const Value: React.FC<React.HTMLAttributes<HTMLParagraphElement>> = ({
  className,
  children,
  ...props
}) => (
  <p {...props} className={cn('tw-col-span-2 tw-break-words', className)}>
    {children}
  </p>
);

const RichValue: React.FC<{ html: string; className?: string }> = ({ html, className }) => (
  <p
    className={cn('tw-col-span-2 tw-whitespace-pre-line tw-break-words', className)}
    dangerouslySetInnerHTML={{
      __html: Str.wrapHrefs(dompurify.sanitize(html)),
    }}
  />
);

const OutcomeRow: React.FC<{ outcome: RequestOutcomeResponse }> = ({ outcome }) => {
  const { t } = useTranslation();
  const unset = t('common:unset', { defaultValue: '-' })!;
  // Needs Attention / Needs Re-edit override the raw status so the pill never
  // shows a stale "Queued" for a flagged outcome (#2510).
  const displayedStatus = resolveDisplayedRequestStatus(outcome);

  return (
    <div
      className={
        'tw-flex tw-flex-col tw-rounded-lg tw-border tw-border-neutral-100 tw-bg-neutral-50 tw-p-4'
      }
    >
      <div
        className={'tw-mb-2 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2'}
      >
        <div className={'tw-break-words tw-text-base tw-font-semibold tw-text-text-strong'}>
          {outcome.title}
        </div>
        <RequestStatusBadge variant={displayedStatus} />
      </div>

      <Row>
        <Label size={'sm'} className={'tw-mb-0'}>
          {t('common:video_type', { defaultValue: 'Video type' })}
        </Label>
        <Value>
          {t(`models/request:type.${outcome.type}`, {
            defaultValue: t('common:unknown', { defaultValue: 'Unknown' }),
          })}
        </Value>
      </Row>

      {outcome.video_template && (
        <Row>
          <Label size={'sm'} className={'tw-mb-0'}>
            {t('common:video_template', { defaultValue: 'Video template' })}
          </Label>
          <Value>{outcome.video_template.title}</Value>
        </Row>
      )}

      <Row>
        <Label size={'sm'} className={'tw-mb-0'}>
          {t('Size')}
        </Label>
        <Value>{outcome.aspect_ratio || unset}</Value>
      </Row>

      {!!outcome.resize?.length && (
        <Row>
          <Label size={'sm'} className={'tw-mb-0'}>
            {t('attributes/request:resolutions.resize', { defaultValue: 'Resizes' })}
          </Label>
          <Value>{outcome.resize.join(', ')}</Value>
        </Row>
      )}

      <Row>
        <Label size={'sm'} className={'tw-mb-0'}>
          {t('common:total_length', { defaultValue: 'Total length' })}
        </Label>
        <Value>{totalLengthToString(outcome.total_length) ?? unset}</Value>
      </Row>

      {outcome.priority && (
        <Row>
          <Label size={'sm'} className={'tw-mb-0'}>
            {t('attributes/request:priority', { defaultValue: 'Priority' })}
          </Label>
          <div className={'tw-col-span-2'}>
            <RequestPriorityBadge variant={outcome.priority} size={'sm'} />
          </div>
        </Row>
      )}

      <Row>
        <Label size={'sm'} className={'tw-mb-0'}>
          {t('common:complexity', { defaultValue: 'Complexity' })}
        </Label>
        <Value>
          {outcome.complexity
            ? t(`models/request:complexity.${outcome.complexity}`, {
                defaultValue: requestComplexityLabels[outcome.complexity] ?? outcome.complexity,
              })
            : unset}
        </Value>
      </Row>

      <Row>
        <Label size={'sm'} className={'tw-mb-0'}>
          {t('common:description', { defaultValue: 'Description' })}
        </Label>
        <RichValue html={outcome.description || unset} />
      </Row>

      <Row>
        <Label size={'sm'} className={'tw-mb-0'}>
          {t('common:reference_videos', { defaultValue: 'Reference videos' })}
        </Label>
        <RichValue html={outcome.examples || unset} />
      </Row>
    </div>
  );
};

const RequestOutcomesSummaryCard: React.FC<React.ComponentProps<typeof CollapsibleCard>> = (
  props,
) => {
  const { t } = useTranslation();
  const [open, setOpen] = useToggle(true);
  const request = useDecoratedRequestContext();

  return (
    <CollapsibleCard
      icon={<Layers />}
      title={t('common:outcomes', { defaultValue: 'Outcomes' })!}
      open={open}
      onOpenChange={setOpen}
      variant={'shadow'}
      {...props}
    >
      <CollapsibleCardContent className={'tw-flex tw-flex-col tw-gap-4'}>
        <QueryErrorResetBoundary>
          {({ reset }) => (
            <ErrorBoundary
              fallbackRender={({ error, resetErrorBoundary }: any) => (
                <SpaceErrorFallback error={error} resetErrorBoundary={resetErrorBoundary} />
              )}
              onReset={reset}
            >
              <React.Suspense
                fallback={<Skeleton className={'tw-h-32 tw-w-full tw-rounded-lg'} />}
              >
                <RequestOutcomesQueryContextProvider
                  id={request.id}
                  filters={deliverableOutcomesFilters}
                >
                  {({ data }) =>
                    !data?.items.length ? null : (
                      <>
                        {data.items.map((outcome) => (
                          <OutcomeRow key={outcome.id} outcome={outcome} />
                        ))}
                      </>
                    )
                  }
                </RequestOutcomesQueryContextProvider>
              </React.Suspense>
            </ErrorBoundary>
          )}
        </QueryErrorResetBoundary>
      </CollapsibleCardContent>
    </CollapsibleCard>
  );
};

export { RequestOutcomesSummaryCard };
